import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Select } from "@/components/erp/ui";
import { Plus, Trash2 } from "lucide-react";

const err = (e) => toast.error(e?.response?.data?.detail || "Gagal");

export const FinAddWo = ({ d, load }) => {
  const [id, setId] = useState("");
  if (!d.is_head) return null;
  const add = async () => { try { await api.put(`/finishing/wo/${id}`, { status: "OPEN" }); toast.success("WO masuk finishing"); setId(""); load(); } catch (e) { err(e); } };
  return (
    <div className="flex flex-wrap gap-2 items-end mb-4" data-testid="fin-add-wo">
      <Field label="Masukkan WO ke finishing"><Select data-testid="fin-add-wo-select" value={id} onChange={(e) => setId(e.target.value)}>
        <option value="">— pilih WO —</option>{d.candidates.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.product}</option>)}
      </Select></Field>
      <Btn data-testid="fin-add-wo-btn" disabled={!id} onClick={add}>Tambah</Btn>
    </div>
  );
};

export const BonusRecap = ({ d }) => {
  const [q, setQ] = useState(""); const [r, setR] = useState(null);
  const load = (qq) => api.get(`/finishing/bonus-recap${qq ? `?quarter=${qq}` : ""}`).then(({ data }) => { setR(data); setQ(data.quarter); }).catch(err);
  if (!d.is_head) return null;
  const rp = (n) => `Rp ${Math.round(n || 0).toLocaleString("id-ID")}`;
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="fin-bonus-recap" onToggle={(e) => { if (e.currentTarget.open && !r) load(""); }}>
      <summary className="font-semibold cursor-pointer">Rekap bonus finishing per kuartal (cek sebelum gajian)</summary>
      {r && (
        <div className="mt-3 text-sm">
          <div className="flex items-center gap-2 mb-2"><Input className="!w-32" data-testid="fin-bonus-quarter" value={q} onChange={(e) => setQ(e.target.value)} placeholder="2026-Q4" /><Btn variant="ghost" data-testid="fin-bonus-load" onClick={() => load(q)}>Lihat</Btn>
            <span className="text-xs text-slate-500">{r.start} s/d {r.end} · dibayar di slip gaji {r.pay_month}</span></div>
          <table className="w-full text-xs" data-testid="fin-bonus-table"><thead><tr className="text-left text-slate-500"><th className="py-1">Nama</th><th>WO</th><th>Bonus final</th><th>Masih berjalan (perkiraan)</th></tr></thead>
            <tbody>{r.people.map((p) => <tr key={p.name} className="border-t"><td className="py-1">{p.name}</td><td>{p.wos}</td><td><b>{rp(p.final)}</b></td><td className="text-slate-500">{rp(p.pending)}</td></tr>)}</tbody></table>
          {!r.people.length && <p className="text-xs text-slate-400 mt-2">Belum ada pekerjaan finishing di kuartal ini.</p>}
          <div className="text-[11px] text-slate-500 mt-2">{r.wos.map((w) => `${w.wo_code} ${w.final ? "selesai" : "berjalan"} · skor ${w.score ?? "—"} · bonus ${rp(w.bonus)}${w.over_time ? " (lewat waktu)" : ""}${w.over_cost ? " (lewat anggaran)" : ""}`).join(" | ")}</div>
        </div>
      )}
    </details>
  );
};

export const FinSettings = ({ d, load }) => {
  const s = d.settings;
  const [v, setV] = useState({ ...s, head_emails: (s.head_emails || []).join(", ") });
  const [ps, setPs] = useState(s.processes || []);
  if (!d.is_owner) return null;
  const save = async () => { try { await api.put("/finishing/settings", { ...v, head_emails: v.head_emails.split(",").map((x) => x.trim()).filter(Boolean), processes: ps.filter((p) => p.label.trim()) }); toast.success("Pengaturan finishing disimpan"); load(); } catch (e) { err(e); } };
  const f = (k, l, type = "number") => <Field label={l}><Input type={type} data-testid={`fin-set-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></Field>;
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="fin-settings">
      <summary className="font-semibold cursor-pointer">Pengaturan finishing (owner)</summary>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        <div className="sm:col-span-3">{f("head_emails", "Email Kepala Finishing (koma)", "text")}</div>
        {f("bonus_pct", "Bonus (% anggaran)")}{f("min_score", "Skor minimal bonus (%)")}{f("invoice_pct", "Anggaran bila tanpa bahan (% invoice)")}
        {f("break_free_min", "Istirahat normal/hari (menit)")}{f("break_max_min", "Istirahat maksimal/hari (menit)")}{f("work_end", "Jam pulang (lembur setelahnya)", "time")}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Istirahat (Lunch, Dinner, Pray, Toilet, Pause) di atas batas normal dipotong dari jam lembur; di atas batas maksimal lembur hari itu otomatis ditolak.</p>
      <ProcEdit ps={ps} setPs={setPs} />
      <Btn className="mt-3" data-testid="fin-set-save" onClick={save}>Simpan</Btn>
      <EmpLink />
    </details>
  );
};

const ProcEdit = ({ ps, setPs }) => (
  <div className="mt-4" data-testid="fin-proc-edit">
    <div className="text-sm font-semibold mb-1">Kegiatan finishing (tombol di panel kerja)</div>
    <div className="flex flex-wrap gap-2">
      {ps.map((p, i) => (
        <div key={p.key || i} className="flex items-center gap-1">
          <Input className="!w-44" data-testid={`fin-proc-label-${i}`} value={p.label} onChange={(e) => setPs(ps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <button type="button" aria-label="hapus kegiatan" data-testid={`fin-proc-del-${i}`} onClick={() => setPs(ps.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400" /></button>
        </div>
      ))}
      <button type="button" data-testid="fin-proc-add" className="text-xs text-sky-700 inline-flex items-center gap-1" onClick={() => setPs([...ps, { key: "", label: "" }])}><Plus className="w-3 h-3" /> kegiatan</button>
    </div>
  </div>
);

const EmpLink = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/finishing/employee-link").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const sync = async () => { try { const r = await api.post("/finishing/employee-link"); toast.success(`${r.data.created.length} data karyawan dibuat`); load(); } catch (e) { err(e); } };
  const miss = rows.filter((r) => !r.employee).length;
  return (
    <div className="mt-5 border-t border-slate-100 pt-4" data-testid="fin-emp-link">
      <div className="font-semibold text-sm">Akun tim ↔ Data karyawan (untuk lembur & bonus di slip gaji)</div>
      <div className="mt-2 space-y-1">
        {rows.map((r) => <EmpRow key={r.email} r={r} reload={load} />)}
      </div>
      {miss > 0 && <Btn className="mt-2" data-testid="fin-emp-sync" onClick={sync}>Buat {miss} data karyawan</Btn>}
      <p className="text-[11px] text-slate-400 mt-2">Isi gaji pokok (bulanan) untuk tim tetap, atau tarif harian untuk tim harian. Tarif harian ikut dihitung sebagai biaya tenaga finishing.</p>
    </div>
  );
};

const EmpRow = ({ r, reload }) => {
  const e = r.employee;
  const [v, setV] = useState({ basic_salary: e?.basic_salary || 0, daily_rate: e?.daily_rate || 0 });
  const save = async () => { try { await api.put(`/finishing/employee-link/${e.id}`, { basic_salary: Number(v.basic_salary) || 0, daily_rate: Number(v.daily_rate) || 0 }); toast.success(`Gaji ${r.name} disimpan`); reload(); } catch (x) { err(x); } };
  const ok = e && (e.basic_salary || e.daily_rate);
  return (
    <div className="flex flex-wrap items-end gap-2 text-xs border-b border-slate-50 py-2" data-testid={`fin-emp-${r.email}`}>
      <div className="min-w-[180px] flex-1"><div className="font-medium">{r.name}</div><div className="text-slate-400">{r.email}</div>
        <div className={ok ? "text-emerald-600" : "text-amber-600"} data-testid={`fin-emp-status-${r.email}`}>{e ? (ok ? "Tersambung" : "Gaji belum diisi") : "Belum ada data karyawan"}</div></div>
      {e && <>
        <Field label="Gaji pokok / bulan"><Input type="number" className="!w-36" data-testid={`fin-emp-basic-${r.email}`} value={v.basic_salary} onChange={(x) => setV({ ...v, basic_salary: x.target.value })} /></Field>
        <Field label="Tarif harian"><Input type="number" className="!w-32" data-testid={`fin-emp-daily-${r.email}`} value={v.daily_rate} onChange={(x) => setV({ ...v, daily_rate: x.target.value })} /></Field>
        <Btn variant="ghost" data-testid={`fin-emp-save-${r.email}`} onClick={save}>Simpan</Btn>
      </>}
    </div>
  );
};

export const WeeklyRecap = ({ d }) => {
  const [r, setR] = useState(null);
  if (!d.is_head) return null;
  const load = () => api.get("/finishing/weekly-recap").then(({ data }) => setR(data)).catch(err);
  const list = (rows, cls) => rows.map((x, i) => <div key={i} className={cls}>{x.date} · {x.name} · {x.wo_code} · {x.process}</div>);
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="fin-weekly-recap" onToggle={(e) => { if (e.currentTarget.open && !r) load(); }}>
      <summary className="font-semibold cursor-pointer">Rekap mingguan tim (dikirim ke Kepala Finishing tiap Sabtu 07:00)</summary>
      {r && (
        <div className="mt-3 text-xs space-y-3">
          <div className="text-slate-500">{r.start} s/d {r.end}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat t="Tugas selesai" v={r.done.length} id="done" /><Stat t="Tidak dikerjakan" v={r.missed.length} id="missed" />
            <Stat t="WO lewat target" v={r.late_wos.length} id="late" /><Stat t="Perkiraan bonus tim" v={`Rp ${r.bonus_est.toLocaleString("id-ID")}`} id="bonus" />
          </div>
          {r.people.map((p) => <div key={p.email} data-testid={`fin-weekly-person-${p.email}`}>{p.name}: <b>{p.done}</b> selesai · <span className="text-amber-600">{p.missed} tidak dikerjakan</span>{p.pending ? ` · ${p.pending} hari ini` : ""}</div>)}
          {r.late_wos.map((w) => <div key={w.wo_code} className="text-red-600">{w.wo_code} {w.product} — target {w.target_date}</div>)}
          {list(r.missed, "text-amber-700")}
          {list(r.pending, "text-slate-500")}
          {list(r.done, "text-emerald-700")}
          {!r.people.length && !r.late_wos.length && <p className="text-slate-400">Belum ada pembagian tugas minggu ini.</p>}
          <Btn variant="ghost" data-testid="fin-weekly-refresh" onClick={load}>Muat ulang</Btn>
        </div>
      )}
    </details>
  );
};

const Stat = ({ t, v, id }) => <div className="rounded-xl bg-slate-50 p-3" data-testid={`fin-weekly-${id}`}><div className="text-slate-500">{t}</div><div className="text-lg font-semibold">{v}</div></div>;
